import React, { useState, useEffect } from 'react';
import { 
    fetchOfferById, 
    fetchAllCategoriesFlat,  // We still need this
    SERVER_BASE_URL 
} from '../api/thamanService';
import Loader from './Loader';
import ErrorMessage from './ErrorMessage';

// Helper to format date
const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    } catch (e) {
        return null;
    }
};

export default function OfferDetailModal({ offerId, onClose }) {
    const [offer, setOffer] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [flatCategories, setFlatCategories] = useState(null);
    const [categoryPath, setCategoryPath] = useState('');

    // This effect still runs ONCE to get the category map
    useEffect(() => {
        const getCategories = async () => {
            try {
                const data = await fetchAllCategoriesFlat();
                setFlatCategories(data);
            } catch (e) {
                console.error("Failed to fetch flat categories", e);
            }
        };
        getCategories();
    }, []); 

    // This effect still fetches the specific offer details
    useEffect(() => {
        if (offerId) {
            const getOfferDetails = async () => {
                try {
                    setIsLoading(true);
                    setError(null);
                    setCategoryPath(''); 
                    const data = await fetchOfferById(offerId);
                    setOffer(data);
                } catch (err) {
                    setError('Failed to load offer details.');
                } finally {
                    setIsLoading(false);
                }
            };
            getOfferDetails();
        }
    }, [offerId]);

    // --- THIS IS THE CORRECTED LOGIC (VERSION 3) ---
    useEffect(() => {
        // Wait until we have the offer, its category_id, AND the category list
        if (offer && offer.category_id && flatCategories && flatCategories.length > 0) {
            
            // 1. Create a fast lookup map (ID -> Category Object)
            const categoryMap = new Map(flatCategories.map(cat => [cat.id, cat]));
            
            const path = [];
            
            // 2. THIS IS THE FIX: We now use offer.category_id to start
            let currentId = offer.category_id; 

            // 3. Loop upwards using parent_id, just as you said
            while (currentId) {
                const category = categoryMap.get(currentId);
                if (!category) break; // Stop if we can't find a parent
                
                path.unshift(category.english_name); // Add the name to the START of the array
                currentId = category.parent_id; // Move up to the parent
            }

            setCategoryPath(path.join(' > ')); // Join the names

        } else if (offer) {
            // Fallback just in case, use the name from the API
            setCategoryPath(offer.category_name || '');
        }
    }, [offer, flatCategories]); // Re-run this logic if offer or categories load
    // --- END OF CORRECTED LOGIC ---


    if (!offerId) return null;

    // --- Helper function to render a detail row (Unchanged) ---
    const DetailRow = ({ label, value }) => {
        if (value == null || value === '') return null;
        return (
            <p className="text-sm">
                <strong className="text-gray-900">{label}:</strong> 
                <span className="text-gray-700 ml-1">{value}</span>
            </p>
        );
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div 
                className="bg-white p-6 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" 
                onClick={(e) => e.stopPropagation()}
            >
                {isLoading && <Loader message="Loading offer..." />}
                {error && <ErrorMessage message={error} />}
                {!isLoading && offer && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* --- IMAGE COLUMN (Unchanged) --- */}
                        <div className="flex flex-col items-center">
                            <img 
                                src={offer.item_image_path ? `${SERVER_BASE_URL}${offer.item_image_path}` : 'https://placehold.co/600x600?text=No+Image'} 
                                alt={offer.english_name}
                                className="w-full max-w-md h-auto object-contain rounded-lg shadow-md"
                            />
                        </div>

                        {/* --- DETAILS COLUMN (Unchanged) --- */}
                        <div className="space-y-4">
                            {/* --- Main Info --- */}
                            <div>
                                <h2 className="text-3xl font-bold text-gray-900">{offer.english_name}</h2>
                                <p className="text-xl text-gray-600 mt-1">{offer.arabic_name}</p>
                            </div>

                            {/* --- Pricing Info --- */}
                            <div className="border-t border-b py-4">
                                <div className="flex items-baseline mb-2">
                                    <span className="text-4xl font-bold text-red-600">{parseFloat(offer.current_price).toFixed(2)} {offer.currency}</span>
                                    {offer.previous_price && (
                                        <span className="ml-3 text-2xl text-gray-400 line-through">{parseFloat(offer.previous_price).toFixed(2)}</span>
                                    )}
                                </div>
                                
                                {offer.price_per_unit && (
                                    <div className="text-lg font-semibold text-gray-800 bg-gray-100 rounded-md p-2 text-center">
                                        {parseFloat(offer.price_per_unit).toFixed(2)} {offer.currency} / {offer.unit_of_measure}
                                    </div>
                                )}
                            </div>

                            {/* --- Discount Info (Unchanged) --- */}
                            {(offer.discount_percentage || offer.amount_saved) && (
                                <div className="flex gap-4">
                                    {offer.discount_percentage && (
                                        <div className="flex-1 bg-green-100 text-green-800 p-3 rounded-lg text-center">
                                            <div className="text-2xl font-bold">{parseFloat(offer.discount_percentage).toFixed(0)}% OFF</div>
                                        </div>
                                    )}
                                    {offer.amount_saved && (
                                        <div className="flex-1 bg-blue-100 text-blue-800 p-3 rounded-lg text-center">
                                            <div className="text-sm font-medium">YOU SAVE</div>
                                            <div className="text-2xl font-bold">{parseFloat(offer.amount_saved).toFixed(2)} {offer.currency}</div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* --- Package Details (Unchanged) --- */}
                            <div className="space-y-1 pt-2">
                                <h4 className="text-md font-semibold text-gray-900 mb-1">Package Details</h4>
                                <DetailRow label="Quantity" value={offer.quantity} />
                                <DetailRow label="Size" value={offer.size} />
                                <DetailRow label="Unit" value={offer.unit} />
                            </div>

                            {/* --- Other Details (Only one line is updated) --- */}
                            <div className="border-t pt-4 space-y-1">
                                <DetailRow label="Store" value={offer.store_name} />
                                <DetailRow label="Brand / Vendor" value={offer.vendor_name} />
                                {/* --- THIS IS THE FIX --- */}
                                <DetailRow label="Category" value={categoryPath} />
                                <DetailRow label="Valid Until" value={formatDate(offer.expire_date)} />
                            </div>

                            <button onClick={onClose} className="mt-6 w-full bg-gray-200 text-gray-800 font-bold py-3 rounded-lg hover:bg-gray-300 transition-colors">
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}